/**
 * Team-folder scope helpers (multi-org BYOS, CC-40).
 *
 * A remote can be PERSONAL (owned by the connecting user, `organization_id`
 * null) or a TEAM remote (scoped to an organization the user is a member of;
 * the server lists it to every live member and re-checks membership on every
 * access). These pure helpers keep the scope selector and the team badge
 * testable without the component.
 /** Waits for the app to finish booting (skeleton overlay disappears). */

export interface OrgSummary {
  id: string;
  name: string;
}

/** Sentinel value for the personal scope in the connect-remote selector. */
export const PERSONAL_SCOPE = "personal";

/** Options for the scope selector — one entry per organization (personal is
 * prepended by the component so its label can be translated at render time). */
export function orgScopeOptions(
  orgs: readonly OrgSummary[] | null | undefined,
): { value: string; label: string }[] {
  return (orgs ?? []).map((org) => ({ value: org.id, label: org.name }));
}

/** Maps the selector value to the `organization_id` the API expects. */
export function scopeToOrganizationId(scope: string | null | undefined): string | null {
  return !scope || scope === PERSONAL_SCOPE ? null : scope;
}

/**
 * Label for the team badge of a remote row.
 *
 * - `null` → personal remote, render NO badge;
 * - `""` → team remote whose org is not in the user's list (e.g. they just
 *   left it but the remote is still visible in a stale cache) — render the
 *   generic "Team" label;
 * - otherwise → the organization's name.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export function teamBadgeLabel(
  organizationId: string | null | undefined,
  orgs: readonly OrgSummary[] | null | undefined,
): string | null {
  if (!organizationId) {
    return null;
  }
  return (orgs ?? []).find((org) => org.id === organizationId)?.name ?? "";
}
